import * as React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Pagination } from '@/components/common/Pagination';
import { TableZoomControls } from '@/components/common/TableZoomControls';
import { useTableZoom } from '@/lib/hooks/useTableZoom';
import { useAuthStore } from '@/features/auth/stores/auth.store';
import { usePaymentsList, usePaymentFilters } from '../hooks';
import {
  usePaymentColumns,
  PaymentsDataTable,
  RecordAdvancePaymentDialog,
} from '../components';

export function PaymentsPage() {
  const { t } = useTranslation();
  const hasAccess = useAuthStore((state) => state.hasAccess);
  const { filters, updateFilter, updateFilters, resetFilters, setPage, setLimit } = usePaymentFilters();

  const { data, isLoading } = usePaymentsList(filters);

  const handleSort = (field: string) => {
    if (filters.orderBy !== field) {
      updateFilters({ orderBy: field, order: 'asc' });
    } else {
      if (filters.order === 'asc') {
        updateFilters({ order: 'desc' });
      } else if (filters.order === 'desc') {
        updateFilters({ orderBy: undefined, order: undefined });
      } else {
        updateFilters({ order: 'asc' });
      }
    }
  };

  // Slot in the title row the table portals its Columns/Filters buttons into.
  const [tableControlsEl, setTableControlsEl] = React.useState<HTMLDivElement | null>(null);
  // Zoom is owned here so its control can sit in the Pagination row (left of
  // the per-page select) while the table applies the same value to its rows.
  const tableZoom = useTableZoom();

  const columns = usePaymentColumns({
    orderBy: filters.orderBy ?? '',
    order: filters.order ?? '',
    onSort: handleSort,
    filters,
  });

  return (
    <div>
      <div className="flex py-2 px-4 justify-between items-center border-b-1">
        <h1 className="text-xl font-semibold">{t('payments')}</h1>
        <div className="flex items-center gap-3">
          <div ref={setTableControlsEl} className="flex items-center gap-2" />
          {hasAccess('payment', 'create') && <RecordAdvancePaymentDialog />}
          {hasAccess('payment-method', 'get') && (
            <Link to="/app/settings/payment-methods">
              <Button variant="outline">{t('paymentMethods')}</Button>
            </Link>
          )}
        </div>
      </div>

      <div className="px-4 pt-4">
        <PaymentsDataTable
          tableZoom={tableZoom}
          controlsContainer={tableControlsEl}
          data={data?.results || []}
          columns={columns}
          filters={filters}
          updateFilter={updateFilter}
          updateFilters={updateFilters}
          onClearFilters={resetFilters}
          isLoading={isLoading}
        />

        <Pagination
          leadingControls={<TableZoomControls zoom={tableZoom} />}
          page={filters.page}
          limit={filters.limit}
          total={data?.meta?.total ?? 0}
          onPageChange={setPage}
          onLimitChange={setLimit}
          isLoading={isLoading}
        />
      </div>
    </div>
  );
}
